// client/src/SchedulePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUnsavedChanges } from "./UnsavedChangesContext";


function formatDateToYYYYMMDD(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function buildStartTimes() {
  // every 15 minutes from 7:00 AM to 5:00 PM inclusive
  const out = [];
  // minutes since midnight: 7:00 = 420, 17:00 = 1020
  for (let m = 7 * 60; m <= 17 * 60; m += 15) {
    const hour24 = Math.floor(m / 60);
    const min = m % 60;

    const ampm = hour24 >= 12 ? "PM" : "AM";
    const hour12raw = hour24 % 12;
    const hour12 = hour12raw === 0 ? 12 : hour12raw;

    const label = `${hour12}:${String(min).padStart(2, "0")} ${ampm}`;
    out.push(label);
  }
  return out;
}

function unionGuestIds(items) {
  const s = new Set();
  (items || []).forEach((it) => (it.guests || []).forEach((g) => s.add(g.id)));
  return s;
}

function normalizeSlotFromStartTime(startTime) {
  const s = (startTime ?? "").toString().toUpperCase();
  if (s.includes("PM")) return "PM";
  if (s.includes("AM")) return "AM";
  return "";
}

function renderActivityText(label) {
  const text = (label || "").toString();
  if (!text.includes("/")) return text;
  const parts = text.split("/");
  return parts.map((part, idx) => (
    <React.Fragment key={`${part}-${idx}`}>
      {idx > 0 ? "/\u200b" : ""}
      {part.trimStart()}
    </React.Fragment>
  ));
}

function Section({
  title,
  items,
  sectionGuestIds,
  startTimes,
  onAddedGuest,
  onRemovedGuest,
  onTransferGuest,
  onSavedCard,
  onRemoveActivity,
  isAdmin,
  pendingGuestRemovals,
  onUndoGuestRemoval,
  onPrintActivity,
  allGuides,
  onGuideAssigned,
  onGuideRemoved,
  onGuideTransferred,
}) {
  return (
    <div style={{ marginTop: 24 }}>
      <h2 style={{ margin: "0 0 12px 0" }}>{title}</h2>

      {items.length === 0 ? (
        <div style={{ padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
          No activities scheduled.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {items.map((item) => (
            <ActivityCard
              key={item.id}
              item={item}
              sectionTitle={title}
              sectionItems={items}
              sectionGuestIds={sectionGuestIds}
              startTimes={startTimes}
              onAddedGuest={onAddedGuest}
              onRemovedGuest={onRemovedGuest}
              onTransferGuest={onTransferGuest}
              onSaved={onSavedCard}
              onRemoveActivity={onRemoveActivity}
              isAdmin={isAdmin}
              pendingGuestRemovals={pendingGuestRemovals}
              onUndoGuestRemoval={onUndoGuestRemoval}
              onPrintActivity={onPrintActivity}
              allGuides={allGuides}
              onGuideAssigned={onGuideAssigned}
              onGuideRemoved={onGuideRemoved}
              onGuideTransferred={onGuideTransferred}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityCard({
  item,
  sectionTitle,
  sectionItems,
  sectionGuestIds,
  startTimes,
  onAddedGuest,
  onRemovedGuest,
  onTransferGuest,
  onSaved,
  onRemoveActivity,
  isAdmin,
  pendingGuestRemovals,
  onUndoGuestRemoval,
  onPrintActivity,
  allGuides,
  onGuideAssigned,
  onGuideRemoved,
  onGuideTransferred,
}) {
  const { setUnsavedChanges } = useUnsavedChanges();
  const spaces = Number(item.spaces ?? 0);
  const guestCount = item.guests?.length ?? 0;
  const remaining = Math.max(0, spaces - guestCount);
  const isFull = spaces > 0 && remaining <= 0;
  const removalNotices = (pendingGuestRemovals || []).filter((p) => p.scheduleId === item.id);

  const currentGuestIds = useMemo(() => new Set((item.guests || []).map((g) => g.id)), [item.guests]);

  // ----- guide state -----
  const [addGuideId, setAddGuideId] = useState('');
  const [addingGuide, setAddingGuide] = useState(false);
  const [guideMsg, setGuideMsg] = useState('');
  const [transferGuideId, setTransferGuideId] = useState(null);
  const [transferGuideTargetId, setTransferGuideTargetId] = useState('');
  const [transferringGuide, setTransferringGuide] = useState(false);

  // Guides eligible for this slot: available for this activity and not already assigned
  const assignedGuideIds = useMemo(() => new Set((item.guides || []).map((g) => g.id)), [item.guides]);
  // Map of guideId → activity name for guides scheduled in other slots in the same section
  const scheduledElsewhereMap = useMemo(() => {
    const map = new Map();
    for (const si of sectionItems || []) {
      if (si.id === item.id) continue;
      for (const g of si.guides || []) {
        if (!map.has(g.id)) map.set(g.id, si.activity);
      }
    }
    return map;
  }, [sectionItems, item.id]);
  const eligibleGuides = useMemo(() => {
    return (allGuides || []).filter(
      (g) =>
        !assignedGuideIds.has(g.id) &&
        (item.activityIds || []).some((aId) => (g.activityIds || []).includes(aId))
    );
  }, [allGuides, assignedGuideIds, item.activityIds]);

  async function assignGuide() {
    if (!addGuideId) return;
    setAddingGuide(true);
    setGuideMsg('');
    try {
      const res = await fetch(`/api/schedule/${item.id}/add-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guideId: addGuideId }),
      });
      const json = await res.json();
      if (!res.ok) {
        setGuideMsg(json?.error || 'Unable to assign guide');
        return;
      }
      const guide = (allGuides || []).find((g) => g.id === addGuideId);
      onGuideAssigned?.(item.id, { id: addGuideId, name: guide?.name || '', activityIds: guide?.activityIds || [] });
      setAddGuideId('');
      setGuideMsg('');
    } catch {
      setGuideMsg('Unable to assign guide');
    } finally {
      setAddingGuide(false);
    }
  }

  async function removeGuideFromSlot(guide) {
    try {
      const res = await fetch(`/api/schedule/${item.id}/remove-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guideId: guide.id }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        alert(json?.error || 'Failed to remove guide');
        return;
      }
      onGuideRemoved?.(item.id, guide.id);
    } catch {
      alert('Failed to remove guide');
    }
  }

  async function transferGuide(guide, targetItem) {
    setTransferringGuide(true);
    try {
      const res = await fetch(`/api/schedule/${item.id}/transfer-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guideId: guide.id, toScheduleId: targetItem.id }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || 'Failed to transfer guide');
        return;
      }
      onGuideTransferred?.(item.id, targetItem.id, guide);
    } catch {
      alert('Failed to transfer guide');
    } finally {
      setTransferringGuide(false);
      setTransferGuideId(null);
      setTransferGuideTargetId('');
    }
  }

  // ----- transfer state -----
  const [transferGuestId, setTransferGuestId] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferring, setTransferring] = useState(false);

  // ----- edit state -----
  const [isEditing, setIsEditing] = useState(false);
  const [editDate, setEditDate] = useState(item.date || ""); // if you don’t return date per item, leave blank
  const [editStartTime, setEditStartTime] = useState(item.startTime || "");
  const [editSpaces, setEditSpaces] = useState(String(spaces || 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // keep edit inputs in sync if list refreshes while not editing
    if (!isEditing) {
      setEditDate(item.date || "");
      setEditStartTime(item.startTime || "");
      setEditSpaces(String(Number(item.spaces ?? 0)));
    }
  }, [item.date, item.startTime, item.spaces, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    const baseDate = item.date || "";
    const baseStart = item.startTime || "";
    const baseSpaces = String(Number(item.spaces ?? 0));
    const hasChanges =
      editDate !== baseDate ||
      editStartTime !== baseStart ||
      editSpaces !== baseSpaces;
    setUnsavedChanges(hasChanges, saveEdits);
  }, [isEditing, editDate, editStartTime, editSpaces, item]);

  async function saveEdits() {
    // slot is derived server-side from start time; we just validate it exists
    const slot = normalizeSlotFromStartTime(editStartTime);
    if (!slot) {
      alert("Start Time must include AM or PM (e.g., 9:15 AM).");
      return false;
    }
    const nSpaces = Number(editSpaces);
    if (!Number.isFinite(nSpaces) || nSpaces < 0) {
      alert("Spaces must be 0 or more.");
      return false;
    }
    if (nSpaces > 0 && guestCount > nSpaces) {
      alert(`Spaces cannot be less than current guest count (${guestCount}).`);
      return false;
    }

    setSaving(true);
    try {
      const res = await fetch(`/api/schedule/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // date is optional—only send if you have it; otherwise omit and backend won't change it
          ...(editDate ? { date: editDate } : {}),
          startTime: editStartTime,
          spaces: nSpaces,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || "Failed to save");
        return false;
      }
      setIsEditing(false);
      setUnsavedChanges(false, null);
      onSaved?.();
      return true;
    } finally {
      setSaving(false);
    }
  }

  function cancelEdits() {
    setIsEditing(false);
    setUnsavedChanges(false, null);
    setEditDate(item.date || "");
    setEditStartTime(item.startTime || "");
    setEditSpaces(String(Number(item.spaces ?? 0)));
  }

  return (
    <div
      style={{
        border: "1px solid var(--stroke)",
        borderRadius: 12,
        padding: 14,
        background: "var(--card)",
        boxShadow: "0 10px 24px rgba(32, 65, 81, 0.12)",
      }}
    >
      {/* Header row: title + edit controls */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>
            <span className="activity-text">{renderActivityText(item.activity)}</span>
            {item.startTime ? ` - ${item.startTime}` : ""}
          </div>

          {item.description ? (
            <div style={{ marginTop: 6, opacity: 0.85 }}>{item.description}</div>
          ) : null}
          
        </div>


        <div style={{ display: "flex", gap: 8, alignItems: "start" }}>
          {isAdmin && (
            <>
              <button onClick={() => onRemoveActivity?.(item)} style={{ height: 32 }}>
                Remove Activity
              </button>
              <button onClick={() => onPrintActivity?.(item)} style={{ height: 32 }}>
                Print
              </button>
              {!isEditing ? (
                <button onClick={() => setIsEditing(true)} style={{ height: 32 }}>
                  Edit
                </button>
              ) : (
                <>
                  <button onClick={saveEdits} disabled={saving} style={{ height: 32 }}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button onClick={cancelEdits} disabled={saving} style={{ height: 32 }}>
                    Cancel
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit panel */}
      {isEditing && isAdmin && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: "1px solid var(--stroke)",
            borderRadius: 10,
            background: "var(--sand)",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "180px 220px 140px", gap: 10, alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Date (optional)</div>
              <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Start Time</div>
              <select value={editStartTime} onChange={(e) => setEditStartTime(e.target.value)}>
                <option value="">Select start time…</option>
                {startTimes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Spaces</div>
              <input
                type="number"
                min="0"
                value={editSpaces}
                onChange={(e) => setEditSpaces(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Guides section */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, opacity: 0.75, marginBottom: 4 }}>
          Guides{item.guides?.length ? ` (${item.guides.length})` : ''}
        </div>
        {item.guides && item.guides.length > 0 ? (
          <div style={{ paddingLeft: 12, display: 'grid', gap: 4 }}>
            {item.guides.map((g) => (
              <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>• {g.name}</span>
                {isAdmin && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      onClick={() => removeGuideFromSlot(g)}
                      style={{ fontSize: 11 }}
                      title="Remove guide from this slot"
                    >
                      Remove
                    </button>
                    {transferGuideId === g.id ? (
                      <>
                        <select
                          value={transferGuideTargetId}
                          onChange={(e) => setTransferGuideTargetId(e.target.value)}
                          style={{ fontSize: 11 }}
                          autoFocus
                        >
                          <option value="">Transfer to…</option>
                          {(sectionItems || [])
                            .filter((it) => it.id !== item.id)
                            .map((it) => {
                              const canGuide = (it.activityIds || []).some((aId) =>
                                (g.activityIds || []).includes(aId)
                              );
                              return (
                                <option key={it.id} value={it.id} disabled={!canGuide}>
                                  {it.activity}{it.startTime ? ` (${it.startTime})` : ''}{!canGuide ? ' [not eligible]' : ''}
                                </option>
                              );
                            })}
                        </select>
                        <button
                          onClick={async () => {
                            if (!transferGuideTargetId) return;
                            const targetItem = (sectionItems || []).find((it) => it.id === transferGuideTargetId);
                            if (!targetItem) return;
                            await transferGuide(g, targetItem);
                          }}
                          disabled={!transferGuideTargetId || transferringGuide}
                          style={{ fontSize: 11 }}
                        >
                          {transferringGuide ? 'Moving…' : 'Transfer'}
                        </button>
                        <button
                          onClick={() => { setTransferGuideId(null); setTransferGuideTargetId(''); }}
                          style={{ fontSize: 11 }}
                          disabled={transferringGuide}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => { setTransferGuideId(g.id); setTransferGuideTargetId(''); }}
                        style={{ fontSize: 11 }}
                        title="Transfer guide to another activity"
                      >
                        Transfer
                      </button>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ paddingLeft: 12, opacity: 0.5, fontSize: 13 }}>No guides assigned</div>
        )}

        {/* Assign guide */}
        {isAdmin && (
          <div style={{ marginTop: 8, paddingLeft: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={addGuideId}
              onChange={(e) => { setAddGuideId(e.target.value); setGuideMsg(''); }}
              style={{ fontSize: 13, minWidth: 160 }}
            >
              <option value="">Assign guide…</option>
              {eligibleGuides.map((g) => {
                const scheduledAt = scheduledElsewhereMap.get(g.id);
                return (
                  <option key={g.id} value={g.id} disabled={!!scheduledAt}>
                    {g.name}{scheduledAt ? ` — scheduled at ${scheduledAt}` : ''}
                  </option>
                );
              })}
            </select>
            <button
              onClick={assignGuide}
              disabled={!addGuideId || addingGuide}
              style={{ fontSize: 13 }}
            >
              {addingGuide ? 'Assigning…' : 'Assign'}
            </button>
            {guideMsg && <span style={{ fontSize: 12, opacity: 0.85 }}>{guideMsg}</span>}
          </div>
        )}
      </div>

      {/* Spaces line */}
      <div style={{ marginTop: 10, opacity: 0.85 }}>
        Spaces: {spaces || 0} • Guests: {guestCount} • Remaining: {spaces ? remaining : 0}
      </div>

      {/* Guest list */}
      <div style={{ marginTop: 10, paddingLeft: 12 }}>
        {guestCount ? (
          item.guests.map((g) => (
            <div
              key={g.id}
              style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 10 }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>
                  • {g.name}
                  {item.activity?.toLowerCase().includes("horseback riding") && g.level ? ` - ${g.level}` : ""}
                </span>
                {g.vip && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "2px 6px",
                      borderRadius: 999,
                      background: "var(--teal)",
                      color: "#fff",
                    }}
                  >
                    VIP
                  </span>
                )}
              </span>
              {isAdmin && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    onClick={() => onRemovedGuest(item, g)}
                    style={{ fontSize: 12 }}
                    title="Remove guest"
                  >
                    Remove
                  </button>
                  {transferGuestId === g.id ? (
                    <>
                      <select
                        value={transferTargetId}
                        onChange={(e) => setTransferTargetId(e.target.value)}
                        style={{ fontSize: 12 }}
                        autoFocus
                      >
                        <option value="">Transfer to…</option>
                        {(sectionItems || [])
                          .filter((it) => it.id !== item.id)
                          .map((it) => {
                            const itFull = it.spaces > 0 && (it.guests?.length ?? 0) >= it.spaces;
                            return (
                              <option key={it.id} value={it.id} disabled={itFull}>
                                {it.activity}{it.startTime ? ` (${it.startTime})` : ''}{itFull ? ' [full]' : ''}
                              </option>
                            );
                          })}
                      </select>
                      <button
                        onClick={async () => {
                          if (!transferTargetId) return;
                          const targetItem = (sectionItems || []).find((it) => it.id === transferTargetId);
                          if (!targetItem) return;
                          setTransferring(true);
                          try {
                            await onTransferGuest?.(item, g, targetItem);
                          } finally {
                            setTransferring(false);
                            setTransferGuestId(null);
                            setTransferTargetId('');
                          }
                        }}
                        disabled={!transferTargetId || transferring}
                        style={{ fontSize: 12 }}
                      >
                        {transferring ? 'Moving…' : 'Transfer'}
                      </button>
                      <button
                        onClick={() => { setTransferGuestId(null); setTransferTargetId(''); }}
                        style={{ fontSize: 12 }}
                        disabled={transferring}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => { setTransferGuestId(g.id); setTransferTargetId(''); }}
                      style={{ fontSize: 12 }}
                      title="Transfer guest to another activity"
                    >
                      Transfer
                    </button>
                  )}
                </span>
              )}
            </div>
          ))
        ) : (
          <div style={{ opacity: 0.75 }}>No guests assigned.</div>
        )}
        {removalNotices.length > 0 && (
          <div style={{ marginTop: 10, border: "1px solid var(--info-border)", background: "var(--info-bg)", borderRadius: 10, padding: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Guest removed</div>
            {removalNotices.map((p) => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                <div>{p.guestName} removed</div>
                <button onClick={() => onUndoGuestRemoval(p.id)}>Undo</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add guest */}
      <AddGuest
        scheduleId={item.id}
        date={item.date}               
        slot={sectionTitle}     
        sectionTitle={sectionTitle}
        sectionGuestIds={sectionGuestIds}
        currentGuestIds={currentGuestIds}
        disabled={!isAdmin || isFull || spaces <= 0}
        remaining={remaining}
        onAdded={onAddedGuest}
        isAdmin={isAdmin}
      />

        {spaces <= 0 && (
        <div style={{ marginTop: 10, color: "#8a3b12" }}>
          Capacity not set (Number of Spaces must be &gt; 0).
        </div>
      )}
      {isFull && spaces > 0 && <div style={{ marginTop: 10, color: "#8a3b12" }}>This activity is full.</div>}
    </div>
  );
}

function AddGuest({ scheduleId, sectionTitle, date, slot, sectionGuestIds, currentGuestIds, disabled, remaining, onAdded, isAdmin }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (disabled) {
      setResults([]);
      setOpen(false);
      return;
    }

    const q = query.trim();
    if (!q) {
      setResults([]);
      setOpen(false);
      setHighlight(-1);
      return;
    }

    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        setBusy(true);
        const res = await fetch(`/api/guests?search=${encodeURIComponent(q)}&date=${encodeURIComponent(date)}&slot=${encodeURIComponent(slot)}`, { signal: controller.signal });
        const json = await res.json();
        const arr = Array.isArray(json) ? json : [];
        setResults(arr);
        setOpen(true);
        setHighlight(arr.length ? 0 : -1);
      } catch (e) {
        if (e.name !== "AbortError") {
          setResults([]);
          setOpen(false);
          setHighlight(-1);
        }
      } finally {
        setBusy(false);
      }
    }, 250);

    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query, disabled]);

  function isBlockedGuest(guestId) {
    if (currentGuestIds.has(guestId)) return { blocked: true, reason: "Already on this activity" };
    if (sectionGuestIds.has(guestId)) return { blocked: true, reason: `Already scheduled in ${sectionTitle}` };
    return { blocked: false, reason: "" };
  }

  async function addGuestById(guestId, guestName) {
    const { blocked, reason } = isBlockedGuest(guestId);
    if (blocked) {
      setMsg(reason);
      return;
    }

    setBusy(true);
    setMsg("");
    try {
      const res = await fetch(`/api/schedule/${scheduleId}/add-guest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ guestId }),
      });
      const json = await res.json();
      if (!res.ok) {
        setMsg(json?.error || "Unable to add guest");
        return;
      }

      setMsg("Added");
      setQuery("");
      setSelectedId("");
      setOpen(false);
      setResults([]);
      setHighlight(-1);
      onAdded?.(scheduleId, { id: guestId, name: guestName || query });
    } catch {
      setMsg("Unable to add guest");
    } finally {
      setBusy(false);
    }
  }

  async function addSelected() {
    if (!selectedId) return;
    await addGuestById(selectedId, query);
  }

  function selectGuest(g) {
    const { blocked, reason } = isBlockedGuest(g.id);
    if (blocked) {
      setMsg(reason);
      return;
    }
    setSelectedId(g.id);
    setQuery(g.name);
    setOpen(false);
    setResults([]);
    setHighlight(-1);
    setMsg("");
  }

  function onKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      if (results.length) setOpen(true);
      return;
    }

    if (e.key === "Escape") {
      setOpen(false);
      setHighlight(-1);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!results.length) return;
      setHighlight((h) => (h + 1) % results.length);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!results.length) return;
      setHighlight((h) => (h - 1 + results.length) % results.length);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      // Enter should add immediately when dropdown open
      if (open && results.length && highlight >= 0) {
        const g = results[highlight];
        const { blocked, reason } = isBlockedGuest(g.id);
        if (blocked) {
          setMsg(reason);
          return;
        }

        // set display
        setSelectedId(g.id);
        setQuery(g.name);
        setOpen(false);
        setResults([]);
        setHighlight(-1);

        // add immediately
        addGuestById(g.id, g.name);
        return;
      }

      addSelected();
    }
  }

  useEffect(() => {
    if (!open || highlight < 0) return;
    const el = itemRefs.current[highlight];
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, open, results.length]);

  return (
    <div style={{ marginTop: 14, position: "relative" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={
            !isAdmin
              ? "Admin only"
              : disabled
                ? "No spaces available"
                : "Type guest name…"
          }
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedId("");
            setMsg("");
          }}
          onFocus={() => {
            if (results.length) setOpen(true);
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          style={{ flex: 1, minWidth: 180 }}
          autoComplete="off"
        />

        <button onClick={addSelected} disabled={disabled || !selectedId || busy}>
          Add
        </button>
      </div>

      {open && !disabled && query.trim() && (
        <div
          ref={listRef}
          style={{
            position: "absolute",
            zIndex: 50,
            left: 0,
            right: 90,
            marginTop: 6,
            border: "1px solid #ccc",
            borderRadius: 8,
            background: "var(--card)",
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          {busy && <div style={{ padding: "8px 10px", opacity: 0.7 }}>Searching…</div>}

          {!busy && results.length === 0 && <div style={{ padding: "8px 10px", opacity: 0.7 }}>No matches</div>}

          {!busy &&
            results.map((g, idx) => {
              const { blocked, reason } = isBlockedGuest(g.id);
              const isHi = idx === highlight;

              return (
                <div
                  key={g.id}
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!blocked) selectGuest(g);
                    else setMsg(reason);
                  }}
                  style={{
                    padding: "8px 10px",
                    cursor: blocked ? "not-allowed" : "pointer",
                    background: isHi ? "var(--selection-bg)" : "transparent",
                    opacity: blocked ? 0.55 : 1,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <span>{g.name}</span>
                  {blocked && <span style={{ fontSize: 12 }}>{reason}</span>}
                </div>
              );
            })}
        </div>
      )}

      <div style={{ marginTop: 8, opacity: 0.85, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <span>{remaining > 0 ? `Remaining spots: ${remaining}` : "This activity is full"}</span>
        {msg && <span>{msg}</span>}
        {!disabled && <span style={{ opacity: 0.7 }}>(Tip: ↑ ↓ + Enter)</span>}
      </div>
    </div>
  );
}

export default function SchedulePage({ isAdmin }) {
  const { requestConfirm } = useUnsavedChanges();
  const startTimes = useMemo(() => buildStartTimes(), []);
  const today = useMemo(() => formatDateToYYYYMMDD(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [pendingGuestRemovals, setPendingGuestRemovals] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [printTarget, setPrintTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ date: today, am: [], pm: [] });
  const [error, setError] = useState("");

  // Add activity form state
  const [activities, setActivities] = useState([]);
  const [guides, setGuides] = useState([]);
  const [addActivityId, setAddActivityId] = useState("");
  const [addDate, setAddDate] = useState(today);
  const [addStartTime, setAddStartTime] = useState("");
  const [addSpaces, setAddSpaces] = useState(5);
  const [adding, setAdding] = useState(false);

  const amGuestIds = useMemo(() => unionGuestIds(data.am), [data.am]);
  const pmGuestIds = useMemo(() => unionGuestIds(data.pm), [data.pm]);

  useEffect(() => {
    function clearPrintTarget() {
      setPrintTarget(null);
    }
    window.addEventListener("afterprint", clearPrintTarget);
    return () => window.removeEventListener("afterprint", clearPrintTarget);
  }, []);

  useEffect(() => {
    if (!printTarget) return;
    const id = window.setTimeout(() => window.print(), 0);
    return () => window.clearTimeout(id);
  }, [printTarget]);

  function updateGuests(scheduleId, updater) {
    setData((prev) => {
      const updateList = (items) =>
        (items || []).map((item) =>
          item.id === scheduleId
            ? { ...item, guests: updater(item.guests || []) }
            : item
        );
      return { ...prev, am: updateList(prev.am), pm: updateList(prev.pm) };
    });
  }

  function handleGuestAdded(scheduleId, guest) {
    if (!scheduleId || !guest) return;
    updateGuests(scheduleId, (guests) => {
      if (guests.some((g) => g.id === guest.id)) return guests;
      return [...guests, guest];
    });
  }

function handleGuestRemoved(scheduleId, guestId) {
  if (!scheduleId || !guestId) return;
  updateGuests(scheduleId, (guests) => guests.filter((g) => g.id !== guestId));
}

  useEffect(() => {
    setAddDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [actRes, guideRes] = await Promise.all([
          fetch("/api/activities"),
          fetch("/api/guides"),
        ]);
        const [actJson, guideJson] = await Promise.all([actRes.json(), guideRes.json()]);
        if (!cancelled) {
          setActivities(Array.isArray(actJson) ? actJson : []);
          setGuides(Array.isArray(guideJson) ? guideJson : []);
        }
      } catch {
        if (!cancelled) setActivities([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(`/api/schedule?date=${encodeURIComponent(selectedDate)}`);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load schedule.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [selectedDate, refreshKey]);
function scheduleDelete(item) {
  if (!isAdmin) return;
  setPendingDeletes((prev) => {
    if (prev.some((p) => p.id === item.id)) return prev;

    const timeoutId = window.setTimeout(async () => {
      try {
        await fetch(`/api/schedule/${item.id}`, { method: "DELETE" });
      } finally {
        setPendingDeletes((p2) => p2.filter((p) => p.id !== item.id));
      }
    }, 10000);

    return [...prev, { id: item.id, activity: item.activity, timeoutId }];
  });
}

function undoDelete(id) {
  setPendingDeletes((prev) => {
    const found = prev.find((p) => p.id === id);
    if (found) window.clearTimeout(found.timeoutId);
    return prev.filter((p) => p.id !== id);
  });
  triggerRefresh();
}

function scheduleGuestRemoval(item, guest) {
  if (!isAdmin || !item?.id || !guest?.id) return;
  setPendingGuestRemovals((prev) => {
    if (prev.some((p) => p.id === `${item.id}:${guest.id}`)) return prev;

    const timeoutId = window.setTimeout(async () => {
      try {
        await fetch(`/api/schedule/${item.id}/remove-guest`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ guestId: guest.id }),
        });
      } finally {
        setPendingGuestRemovals((p2) => p2.filter((p) => p.id !== `${item.id}:${guest.id}`));
      }
    }, 10000);

    return [
      ...prev,
      {
        id: `${item.id}:${guest.id}`,
        scheduleId: item.id,
        guestId: guest.id,
        guestName: guest.name || "Guest",
        activity: item.activity || "",
        timeoutId,
      },
    ];
  });
  handleGuestRemoved(item.id, guest.id);
}

function undoGuestRemoval(id) {
  let restored = null;
  setPendingGuestRemovals((prev) => {
    restored = prev.find((p) => p.id === id) || null;
    if (restored) window.clearTimeout(restored.timeoutId);
    return prev.filter((p) => p.id !== id);
  });
  if (restored) {
    handleGuestAdded(restored.scheduleId, { id: restored.guestId, name: restored.guestName });
  }
}

function updateGuidesList(scheduleId, updater) {
  setData((prev) => {
    const updateList = (items) =>
      (items || []).map((item) =>
        item.id === scheduleId
          ? { ...item, guides: updater(item.guides || []) }
          : item
      );
    return { ...prev, am: updateList(prev.am), pm: updateList(prev.pm) };
  });
}

function handleGuideAssigned(scheduleId, guide) {
  if (!scheduleId || !guide) return;
  updateGuidesList(scheduleId, (gs) => {
    if (gs.some((g) => g.id === guide.id)) return gs;
    return [...gs, guide];
  });
}

function handleGuideRemoved(scheduleId, guideId) {
  if (!scheduleId || !guideId) return;
  updateGuidesList(scheduleId, (gs) => gs.filter((g) => g.id !== guideId));
}

function handleGuideTransferred(fromId, toId, guide) {
  handleGuideRemoved(fromId, guide.id);
  handleGuideAssigned(toId, guide);
}

const pendingIds = useMemo(() => new Set(pendingDeletes.map((p) => p.id)), [pendingDeletes]);

const amItems = useMemo(() => (data.am || []).filter((it) => !pendingIds.has(it.id)), [data.am, pendingIds]);
  const pmItems = useMemo(() => (data.pm || []).filter((it) => !pendingIds.has(it.id)), [data.pm, pendingIds]);
  function triggerRefresh() {
    setRefreshKey((x) => x + 1);
  }

  function csvEscape(value) {
    const s = (value ?? "").toString();
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  function exportCsv() {
    function guestLabel(item, guest) {
      if (!guest) return "";
      const isHorseback = item.activity?.toLowerCase().includes("horseback riding");
      return isHorseback && guest.level ? `${guest.name} - ${guest.level}` : guest.name;
    }

    function buildCsv(items) {
      const headers = items.map((item) => item.activity || "");
      const maxGuests = items.reduce((max, item) => Math.max(max, (item.guests || []).length), 0);
      const rows = [headers];
      for (let i = 0; i < maxGuests; i += 1) {
        rows.push(items.map((item) => guestLabel(item, (item.guests || [])[i])));
      }
      return rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    }

    function downloadCsv(label, items) {
      const csv = buildCsv(items);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule-${selectedDate}-${label}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }

    downloadCsv("am", amItems);
    downloadCsv("pm", pmItems);
  }

  function guestLabelForActivity(item, guest) {
    if (!guest) return "";
    const isHorseback = item.activity?.toLowerCase().includes("horseback riding");
    return isHorseback && guest.level ? `${guest.name} - ${guest.level}` : guest.name;
  }

async function addScheduleItem() {
    if (!isAdmin) return;
    if (!addActivityId || !addDate || !addStartTime) {
      alert("Pick activity, date, and start time.");
      return;
    }
    const slot = normalizeSlotFromStartTime(addStartTime);
    if (!slot) {
      alert("Start Time must include AM or PM.");
      return;
    }

    setAdding(true);
    try {
      const res = await fetch("/api/schedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: addDate,
          activityId: addActivityId,
          startTime: addStartTime,
          spaces: Number(addSpaces ?? 0),
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || "Failed to add activity");
        return;
      }

      setAddActivityId("");
      setAddStartTime("");
      setAddSpaces("");
      triggerRefresh();
    } finally {
      setAdding(false);
    }
  }

async function removeGuest(item, guest) {
    if (!isAdmin) return;
    scheduleGuestRemoval(item, guest);
  }

async function handleGuestTransfer(fromItem, guest, toItem) {
  if (!isAdmin || !fromItem?.id || !guest?.id || !toItem?.id) return;
  try {
    const removeRes = await fetch(`/api/schedule/${fromItem.id}/remove-guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guestId: guest.id }),
    });
    if (!removeRes.ok) {
      const err = await removeRes.json().catch(() => ({}));
      throw new Error(err?.error || 'Failed to remove guest from activity');
    }

    const addRes = await fetch(`/api/schedule/${toItem.id}/add-guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guestId: guest.id }),
    });
    if (!addRes.ok) {
      const err = await addRes.json().catch(() => ({}));
      // Undo the remove so the guest isn't lost
      await fetch(`/api/schedule/${fromItem.id}/add-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestId: guest.id }),
      });
      throw new Error(err?.error || 'Failed to add guest to new activity');
    }

    handleGuestRemoved(fromItem.id, guest.id);
    handleGuestAdded(toItem.id, { id: guest.id, name: guest.name, level: guest.level, vip: guest.vip });
    setToast({ message: `${guest.name} moved to ${toItem.activity}` });
    setTimeout(() => setToast(null), 4000);
  } catch (e) {
    alert(`Transfer failed: ${e.message || 'Please try again.'}`);
    triggerRefresh();
  }
}
const [toast, setToast] = useState(null);
const [cloneOpen, setCloneOpen] = useState(false);
const [cloneToDate, setCloneToDate] = useState(selectedDate);
const [cloning, setCloning] = useState(false);

useEffect(() => setCloneToDate(selectedDate), [selectedDate]);

async function cloneDay() {
  if (!isAdmin) return;
  if (!cloneToDate) return;
  setCloning(true);
  try {
    const res = await fetch("/api/schedule/clone-day", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fromDate: selectedDate, toDate: cloneToDate }),
    });
    const json = await res.json();
    if (!res.ok) {
      alert(json?.error || json?.details || "Failed to clone day");
      return;
    }
    setCloneOpen(false);
    setSelectedDate(cloneToDate);
    triggerRefresh();
  } finally {
    setCloning(false);
  }
}

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 20 }}>
      <style>{`
        .print-only {
          display: none;
        }
        @media print {
          body {
            background: #fff !important;
          }
          .print-only {
            display: block;
          }
          .screen-only {
            display: none !important;
          }
          .print-only {
            color: #1b1f24;
          }
          .print-page {
            padding: 8px 0;
          }
          .print-section {
            margin-bottom: 18px;
            padding-top: 8px;
            border-top: 1px solid #d0d7d8;
          }
          .print-page-break {
            page-break-before: always;
            break-before: page;
          }
          .print-section-title {
            font-size: 13px;
            letter-spacing: 1px;
            text-transform: uppercase;
            font-weight: 800;
            margin-bottom: 8px;
            color: #204151;
          }
          .print-activity {
            border: 1px solid #d6dddf;
            border-radius: 8px;
            padding: 10px 12px;
            margin-bottom: 10px;
            background: #f8fafb;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          @media print {
            .print-activity-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
            }
            .print-activity {
              margin-bottom: 0;
            }
          }
          .print-activity-title {
            font-weight: 700;
            display: flex;
            justify-content: space-between;
            gap: 10px;
          }
          .print-activity-time {
            font-weight: 600;
            opacity: 0.7;
            white-space: nowrap;
          }
          .print-guest-list {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .print-guests {
            margin-top: 4px;
            padding-left: 16px;
            columns: 2;
            column-gap: 18px;
          }
          .print-guests li {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="print-only">
        <div className="print-page">
          {printTarget ? (
            <div className="print-section">
              <div className="print-section-title">
                {printTarget.activity}
                {printTarget.startTime ? ` — ${printTarget.startTime}` : ""}
              </div>
              {printTarget.guides?.length ? (
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  <strong>Guides:</strong> {printTarget.guides.map((g) => g.name).join(", ")}
                </div>
              ) : null}
              {printTarget.guests?.length ? (
                <ul className="print-guests print-guest-list">
                  {printTarget.guests.map((g) => (
                    <li key={g.id}>{guestLabelForActivity(printTarget, g)}</li>
                  ))}
                </ul>
              ) : (
                <div className="print-guests" style={{ opacity: 0.7 }}>No guests</div>
              )}
            </div>
          ) : (
            <>
              <div className="print-section">
                <div className="print-section-title">AM</div>
                {(amItems || []).length === 0 && <div style={{ opacity: 0.7 }}>No activities scheduled.</div>}
                <div className="print-activity-grid">
                  {(amItems || []).map((item) => (
                    <div key={item.id} className="print-activity">
                      <div className="print-activity-title">
                        <span>{item.activity}</span>
                        {item.startTime ? <span className="print-activity-time">{item.startTime}</span> : null}
                      </div>
                      {item.guides?.length ? (
                        <div style={{ fontSize: 11, marginTop: 2, marginBottom: 2 }}>
                          <strong>Guides:</strong> {item.guides.map((g) => g.name).join(", ")}
                        </div>
                      ) : null}
                      {item.guests?.length ? (
                        <ul className="print-guests print-guest-list">
                          {item.guests.map((g) => (
                            <li key={g.id}>{guestLabelForActivity(item, g)}</li>
                          ))}
                        </ul>
                      ) : (
                        <div className="print-guests" style={{ opacity: 0.7 }}>No guests</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="print-section print-page-break">
                <div className="print-section-title">PM</div>
                {(pmItems || []).length === 0 && <div style={{ opacity: 0.7 }}>No activities scheduled.</div>}
                <div className="print-activity-grid">
                  {(pmItems || []).map((item) => (
                    <div key={item.id} className="print-activity">
                      <div className="print-activity-title">
                        <span>{item.activity}</span>
                        {item.startTime ? <span className="print-activity-time">{item.startTime}</span> : null}
                      </div>
                      {item.guides?.length ? (
                        <div style={{ fontSize: 11, marginTop: 2, marginBottom: 2 }}>
                          <strong>Guides:</strong> {item.guides.map((g) => g.name).join(", ")}
                        </div>
                      ) : null}
                      {item.guests?.length ? (
                        <ul className="print-guests print-guest-list">
                          {item.guests.map((g) => (
                            <li key={g.id}>{guestLabelForActivity(item, g)}</li>
                          ))}
                        </ul>
                      ) : (
                        <div className="print-guests" style={{ opacity: 0.7 }}>No guests</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="screen-only">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h1 style={{ margin: 0 }}>Schedule</h1>
            {!isAdmin && <div style={{ fontSize: 12, opacity: 0.7 }}>Read-only mode</div>}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => window.print()}>Print</button>
            <button onClick={exportCsv}>Export CSV</button>
            <button onClick={() => isAdmin && setCloneOpen(true)} disabled={!isAdmin}>
              Clone Day
            </button>
            <label htmlFor="date" style={{ fontWeight: 700 }}>
              Date
            </label>
            <input
              id="date"
              type="date"
              value={selectedDate}
              onChange={(e) => {
                const next = e.target.value;
                requestConfirm(() => setSelectedDate(next));
              }}
            />
          </div>
          {pendingDeletes.length > 0 && (
            <div style={{ marginTop: 12, border: "1px solid var(--warning-border)", background: "var(--warning-bg)", borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Activity removed</div>
              {pendingDeletes.map((p) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                  <div>{p.activity}</div>
                  <button onClick={() => undoDelete(p.id)}>Undo</button>
                </div>
              ))}
              <div style={{ marginTop: 6, opacity: 0.7 }}>This will be permanently deleted in 10 seconds.</div>
            </div>
          )}

        </div>


{cloneOpen && isAdmin && (
  <div style={{ marginTop: 10, border: "1px solid #ddd", borderRadius: 10, padding: 12 }}>
    <div style={{ fontWeight: 800, marginBottom: 8 }}>Clone {selectedDate} →</div>
    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
      <input type="date" value={cloneToDate} onChange={(e) => setCloneToDate(e.target.value)} />
      <button onClick={cloneDay} disabled={cloning}>
        {cloning ? "Cloning…" : "Clone"}
      </button>
      <button onClick={() => setCloneOpen(false)} disabled={cloning}>Cancel</button>
    </div>
    <div style={{ marginTop: 8, opacity: 0.7 }}>
      Copies activities/spaces/start time. Does not copy guests.
    </div>
  </div>
)}

      {/* Add Activity - top of page */}
      <div style={{ marginTop: 14, border: "1px solid #ddd", borderRadius: 10, padding: 12, opacity: isAdmin ? 1 : 0.6 }}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Add Activity</div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 160px 190px 120px auto",
            gap: 10,
            alignItems: "center",
          }}
        >
          <select value={addActivityId} onChange={(e) => setAddActivityId(e.target.value)} disabled={!isAdmin}>
            <option value="">Select activity…</option>
            {activities.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          <input type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} disabled={!isAdmin} />

          <select value={addStartTime} onChange={(e) => setAddStartTime(e.target.value)} disabled={!isAdmin}>
            <option value="">Start time…</option>
            {startTimes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <input
            type="number"
            min="0"
            value={addSpaces}
            onChange={(e) => setAddSpaces(e.target.value)}
            placeholder="Spaces"
            disabled={!isAdmin}
          />

          <button onClick={addScheduleItem} disabled={!isAdmin || adding}>
            {adding ? "Adding…" : "Add"}
          </button>
        </div>

        
      </div>

      {loading && <div style={{ marginTop: 16 }}>Loading…</div>}

      {error && <div style={{ marginTop: 16, color: "crimson" }}>{error}</div>}

      {!loading && !error && (
        <>
          <Section
            title="AM"
            items={amItems}
            onRemoveActivity={(item) => setConfirmDelete(item)}
            sectionGuestIds={amGuestIds}
            startTimes={startTimes}
            onAddedGuest={handleGuestAdded}
            onRemovedGuest={removeGuest}
            onTransferGuest={handleGuestTransfer}
            onSavedCard={triggerRefresh}
            isAdmin={isAdmin}
            pendingGuestRemovals={pendingGuestRemovals}
            onUndoGuestRemoval={undoGuestRemoval}
            onPrintActivity={(item) => setPrintTarget(item)}
            allGuides={guides}
            onGuideAssigned={handleGuideAssigned}
            onGuideRemoved={handleGuideRemoved}
            onGuideTransferred={handleGuideTransferred}
          />
          <Section
            title="PM"
            items={pmItems}
            onRemoveActivity={(item) => setConfirmDelete(item)}
            sectionGuestIds={pmGuestIds}
            startTimes={startTimes}
            onAddedGuest={handleGuestAdded}
            onRemovedGuest={removeGuest}
            onTransferGuest={handleGuestTransfer}
            onSavedCard={triggerRefresh}
            isAdmin={isAdmin}
            pendingGuestRemovals={pendingGuestRemovals}
            onUndoGuestRemoval={undoGuestRemoval}
            onPrintActivity={(item) => setPrintTarget(item)}
            allGuides={guides}
            onGuideAssigned={handleGuideAssigned}
            onGuideRemoved={handleGuideRemoved}
            onGuideTransferred={handleGuideTransferred}
          />
        </>
      )}
      {confirmDelete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "grid",
            placeItems: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "var(--card)",
              borderRadius: 14,
              padding: 20,
              width: "min(420px, 92vw)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 18 }}>Remove activity?</div>
            <div style={{ marginTop: 8, opacity: 0.8 }}>
              This will remove <strong>{confirmDelete.activity || "this activity"}</strong> from the schedule.
            </div>
            <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmDelete(null)}
                style={{ background: "#8f9aa3" }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  scheduleDelete(confirmDelete);
                  setConfirmDelete(null);
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            top: 16,
            right: 20,
            background: 'var(--card)',
            color: 'var(--ink)',
            border: '1px solid var(--stroke)',
            borderRadius: 12,
            padding: '10px 14px',
            boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            zIndex: 1200,
            maxWidth: 360,
          }}
        >
          <div style={{ fontSize: 13 }}>{toast.message}</div>
          <button onClick={() => setToast(null)} style={{ fontSize: 11 }}>Close</button>
        </div>
      )}
      </div>
    </div>
  );
}
